// Enums matching backend (string values — backend uses JsonStringEnumConverter)
export enum ScriptElementType {
  Text = 'Text',
  SurveyQuestion = 'SurveyQuestion',
  TagAssignment = 'TagAssignment',
  ConditionalBranch = 'ConditionalBranch',
}

export enum ContactMethod {
  Door = 'Door',
  Phone = 'Phone',
  Event = 'Event',
  Text = 'Text',
  Email = 'Email',
}

export enum ResultStatus {
  Completed = 'Completed',
  Busy = 'Busy',
  NoAnswer = 'NoAnswer',
  LeftMessage = 'LeftMessage',
  Moved = 'Moved',
  Refused = 'Refused',
  Callback = 'Callback',
  Deceased = 'Deceased',
  Inaccessible = 'Inaccessible',
  LanguageBarrier = 'LanguageBarrier',
}

export enum VisitActionFlag {
  LeftLiterature = 'LeftLiterature',
  LeftMessage = 'LeftMessage',
  ScheduleCallback = 'ScheduleCallback',
  ComeBackLater = 'ComeBackLater',
  // System flags (Phase 4 door flow); never offered as canvasser-selectable checkboxes.
  NotAtThisAddress = 'NotAtThisAddress',
  NotAtThisWorksite = 'NotAtThisWorksite',
  NotAtWork = 'NotAtWork',
}

export const VISIT_ACTION_FLAG_LABELS: Record<VisitActionFlag, string> = {
  [VisitActionFlag.LeftLiterature]: 'Left Literature',
  [VisitActionFlag.LeftMessage]: 'Left Message',
  [VisitActionFlag.ScheduleCallback]: 'Schedule Callback',
  [VisitActionFlag.ComeBackLater]: 'Come Back Later',
  [VisitActionFlag.NotAtThisAddress]: "Doesn't live here",
  [VisitActionFlag.NotAtThisWorksite]: "Doesn't work here",
  [VisitActionFlag.NotAtWork]: 'Not at work today',
}

// Script DTOs
export interface ScriptSummaryDto {
  id: string
  label: string
  description?: string | null
  isActive: boolean
  elementCount: number
  createdAt: string
  createdByName: string
}

export interface ScriptDetailDto {
  id: string
  label: string
  description?: string | null
  isActive: boolean
  ownerCommitteeId: string
  createdAt: string
  updatedAt: string
  createdBy: string
  createdByName: string
  elements: ScriptElementDto[]
  resultSettings: CanvasResultSettingsDto[]
}

export interface SaveScriptRequest {
  label: string
  description?: string | null
  isActive?: boolean
  elements: SaveScriptElementRequest[]
  resultSettings: SaveCanvasResultSettingsRequest[]
}

// Script Element DTOs
export interface ScriptElementDto {
  id: string
  elementType: ScriptElementType
  displayOrder: number
  parentElementId?: string | null
  nextElementId?: string | null
  // Type-specific fields
  textContent?: string | null
  textContentSpanish?: string | null
  surveyQuestionId?: string | null
  surveyQuestionLabel?: string | null
  tagId?: string | null
  tagLabel?: string | null
  conditionalField?: string | null
  conditionalOperator?: string | null
  conditionalValue?: string | null
  nextElementOnTrue?: string | null
  nextElementOnFalse?: string | null
  // Answer branching for SurveyQuestion elements (answerId → targetElementId)
  answerBranches?: Record<string, string> | null
  // Prompt conditional fields
  promptText?: string | null
  promptResponses?: PromptResponseDto[] | null
}

export interface SaveScriptElementRequest {
  clientId: string  // Frontend-generated UUID
  elementType: ScriptElementType
  displayOrder: number
  parentClientId?: string | null
  nextElementClientId?: string | null
  // Type-specific fields
  textContent?: string | null
  textContentSpanish?: string | null
  surveyQuestionId?: string | null
  tagId?: string | null
  conditionalField?: string | null
  conditionalOperator?: string | null
  conditionalValue?: string | null
  nextElementOnTrueClientId?: string | null
  nextElementOnFalseClientId?: string | null
  // Answer branching: answerId (real GUID) → targetElementClientId
  answerBranchesClientIds?: Record<string, string> | null
  // Prompt conditional fields
  promptText?: string | null
  promptResponses?: { label: string; targetElementClientId?: string | null }[] | null
}

// Form state with clientId for managing elements before save
export interface ScriptElementFormState {
  clientId: string
  elementType: ScriptElementType
  displayOrder: number
  // Transient client-only flag: true for a just-added element so its card
  // mounts expanded. Never mapped into SaveScriptElementRequest (not persisted).
  isNew?: boolean
  parentClientId?: string | null
  nextElementClientId?: string | null
  // Type-specific fields
  textContent?: string
  textContentSpanish?: string
  surveyQuestionId?: string | null
  tagId?: string | null
  conditionalField?: string
  conditionalOperator?: string
  conditionalValue?: string
  nextElementOnTrueClientId?: string | null
  nextElementOnFalseClientId?: string | null
  // Answer branching: answerId → targetElementClientId
  answerBranches?: Record<string, string>
  // Prompt conditional fields
  promptText?: string
  promptResponses?: { label: string; targetClientId?: string | null }[]
}

// Canvas Result Settings DTOs
export interface CanvasResultSettingsDto {
  id: string
  contactMethod: ContactMethod
  allowedResults: ResultStatus[]
  allowedFlags: VisitActionFlag[]
}

export interface SaveCanvasResultSettingsRequest {
  contactMethod: ContactMethod
  allowedResults: ResultStatus[]
  allowedFlags: VisitActionFlag[]
}

export interface CanvasResultSettingsFormState {
  contactMethod: ContactMethod
  allowedResults: ResultStatus[]
  allowedFlags: VisitActionFlag[]
}

// System Defaults DTO
export interface DefaultResultSettingsDto {
  contactMethod: ContactMethod
  defaultResults: ResultStatus[]
  defaultFlags: VisitActionFlag[]
}

// Picker DTOs
export interface TagPickerDto {
  id: string
  label: string
  tagType?: string | null
  isVisible: boolean
  canBeAssigned: boolean
}

export interface QuestionPickerDto {
  id: string
  label: string
  question: string
  surveyGroupLabel?: string | null
}

// Branching DTOs
export interface PromptResponseDto {
  label: string
  targetElementId?: string | null
}

export interface AnswerPickerDto {
  id: string
  answerText: string
  displayOrder: number
}

export interface QuestionAnswerPickerDto {
  questionId: string
  answers: AnswerPickerDto[]
}

export type ConditionalBranchType = 'Answer' | 'Prompt' | 'Tag' | 'Attribute'

export const CONDITIONAL_BRANCH_TYPE_LABELS: Record<ConditionalBranchType, string> = {
  Answer: 'Answer-Based',
  Prompt: 'Canvasser Prompt',
  Tag: 'Tag Presence',
  Attribute: 'Predicted Attribute',
}

export interface AttributePickerDto {
  key: string
  label: string
  options: { value: string; label: string }[]
}

// Helper constants
export const ELEMENT_TYPE_LABELS: Record<ScriptElementType, string> = {
  [ScriptElementType.Text]: 'Text',
  [ScriptElementType.SurveyQuestion]: 'Survey Question',
  [ScriptElementType.TagAssignment]: 'Tag Assignment',
  [ScriptElementType.ConditionalBranch]: 'Conditional Branch',
}

export const ELEMENT_TYPE_COLORS: Record<ScriptElementType, string> = {
  [ScriptElementType.Text]: 'blue',
  [ScriptElementType.SurveyQuestion]: 'green',
  [ScriptElementType.TagAssignment]: 'purple',
  [ScriptElementType.ConditionalBranch]: 'orange',
}

export const CONTACT_METHOD_LABELS: Record<ContactMethod, string> = {
  [ContactMethod.Door]: 'Door',
  [ContactMethod.Phone]: 'Phone',
  [ContactMethod.Event]: 'Event',
  [ContactMethod.Text]: 'Text',
  [ContactMethod.Email]: 'Email',
}

export const RESULT_STATUS_LABELS: Record<ResultStatus, string> = {
  [ResultStatus.Completed]: 'Completed',
  [ResultStatus.Busy]: 'Busy',
  [ResultStatus.NoAnswer]: 'Not home',
  [ResultStatus.LeftMessage]: 'Left message',
  [ResultStatus.Moved]: 'Moved',
  [ResultStatus.Refused]: 'Refused',
  [ResultStatus.Callback]: 'Callback',
  [ResultStatus.Deceased]: 'Deceased',
  [ResultStatus.Inaccessible]: "Couldn't knock",
  [ResultStatus.LanguageBarrier]: 'Language barrier',
}

// ========== Lane-Based Builder Types ==========

export type LaneBranchType = 'true' | 'false' | 'answer' | 'prompt-response'

export interface ScriptLane {
  id: string
  label: string
  colorScheme: string
  elements: ScriptElementFormState[]
  // Where this lane branches from
  sourceElementClientId?: string
  sourceBranchType?: LaneBranchType
  sourceBranchKey?: string // answer ID or prompt response label
  // Where this lane merges back to
  mergeTargetClientId?: string // element in another lane this lane rejoins at
}

// Color palette for branch lanes (main lane always uses 'blue')
// Avoids colors used in ELEMENT_TYPE_COLORS (green, purple, orange, blue) to prevent confusion
// Uses colors with good contrast for white text on solid backgrounds
export const LANE_COLOR_PALETTE = [
  'teal', 'pink', 'cyan', 'red', 'messenger', 'telegram', 'twitter', 'linkedin',
] as const
